import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
  Button,
  Checkbox,
  Portal,
  createListCollection,
  HStack,
  Input,
  Select,
  Spinner,
  VStack,
  NumberInput,
  Switch,
  Text,
} from "@chakra-ui/react";
import { useForm } from "@/uses/useForm";
import { useApi } from "@/uses/useApi";
import { Field } from "@/components";
import { useLanguage } from "@/contexts/languageContext";
import { CertificatesInput } from "@/components/input/CertificatesInput";

const salaryRanges = ["none", "low", "medium", "high"];
const roles = [
  { id: 0, name: "ADMIN" },
  { id: 2, name: "PLAYER" },
  { id: 3, name: "YOUNG" },
];

export const CreateOrEditUser = (props) => {
  const userId = props?.id;

  const { t } = useLanguage();
  const [state, setState] = useState(-1);
  const [user, setUser] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [checkPartners, setCheckPartners] = useState(null);
  const [isResent, setIsResent] = useState(false);
  const [removeDeletedAt, setRemoveDeletedAt] = useState(false);
  const api = useApi();
  const {
    formData,
    formFieldErrors,
    handleInputChange,
    handleInputChanges,
    handleFormSubmit,
  } = useForm(
    {
      email: "",
      password: "",
      firstname: "",
      lastname: "",
      phone: "",
      club: "",
      division: "",
      salaryRange: null,
      authorization: false,
      roleId: 2,
    },
    {
      email: {
        required: {
          value: true,
          message: t("errors.pleaseFillField"),
        },
      },
    },
    );

  const handleResend = () => {

    api.ApiService.rawPut(`/partners/resend/${userId}`, {}).then((res) => {
      if (res?.data?.type === "success" && res?.data.value.resend) {
        setIsResent(true)
      }
    })
  }

  const getUser = useCallback((userId) => {
    if (userId) {
      api.ApiService.getOne("user", userId, {
        attributes: [
          "id",
          "email",
          "password",
          "firstname",
          "lastname",
          "phone",
          "club",
          "division",
          "salaryRange",
          "authorization",
          "activated",
          "certificates",
          "roleId",
        ],
      }).then((res) => {
        if (res?.data?.type === "success" && res?.data?.value) {
          setUser(res.data.value);

          api.ApiService.rawGet(`/partners/check/${userId}`, {}).then((res) => {
            if (res?.data?.type === "success" && res?.data?.value) {
              setCheckPartners(res?.data?.value);
            }
          });
        }
      });
    }
  }, [api.ApiService])

  useEffect(() => {
    setState(() => 0);
    api.ApiService.get("club", { getAll: true }).then((res) => {
      if (res?.data?.type === "success") setClubs(res.data.value);
    });
    api.ApiService.get("division", { getAll: true }).then((res) => {
      if (res?.data?.type === "success") setDivisions(res.data.value);
    });
    getUser(userId);
  }, [userId]);

  useEffect(() => {
    if (user !== null) {
      handleInputChanges({
        ...user,
      });
    }
    /** If tags are retrieved, and we're in create mode
     * or article and tags are retrieved, and we're in edit mode
     */
    if (!userId || user !== null) {
      setState(() => 1);
    }
  }, [user, userId]);

  const divisionsCollection = createListCollection({
    items: divisions.map((division) => ({
      label: division.label,
      value: division.label,
    })),
  });

  const filteredClubs = clubs.filter((club) => {
    if (!formData.division) {
      return false;
    }

    return club.labelDivision === formData.division || club.division?.label === formData.division;
  });

  const clubsCollection = createListCollection({
    items: filteredClubs.map((club) => ({
      label: club.label,
      value: club.label,
    })),
  });

  const salaryRangesCollection = createListCollection({
    items: salaryRanges.map((salaryRange) => ({
      label: t(`salary.${salaryRange}`),
      value: salaryRange,
    })),
  });

  const rolesCollection = createListCollection({
    items: roles.map((role) => ({
      label: t(`roles.${role.name.toLowerCase()}`),
      value: role.id,
    })),
  });

  return (
    <VStack h="100%" overflow="hidden" p="1.5rem 0" gap="5vh" w="100%">
      {state === 1 ? (
        <VStack
          overflow="auto"
          flex={1}
          gap="5"
          p="5"
          w="100%"
          align="stretch"
          justify="flex-start"
        >
          <HStack>
            <Field
              label={t('wrappers.nonMandatoryLabel', { value: t('fields.firstname') })}
              errors={formFieldErrors.firstname}
            >
              <Input
                value={formData.firstname ?? ""}
                onChange={(e) => {
                  handleInputChange("firstname", e?.target?.value ?? "");
                }}
                placeholder={t("users.userFirstname")}
              />
            </Field>
            <Field
              label={t('wrappers.nonMandatoryLabel', { value: t('fields.lastname') })}
              errors={formFieldErrors.lastname}
            >
              <Input
                value={formData.lastname ?? ""}
                onChange={(e) => {
                  handleInputChange("lastname", e?.target?.value ?? "");
                }}
                placeholder={t("users.userLastname")}
              />
            </Field>
            <Field
              label={t('wrappers.nonMandatoryLabel', { value: t('fields.phone') })}
              errors={formFieldErrors.phone}
            >
              <Input
                value={formData.phone ?? ""}
                onChange={(e) => {
                  handleInputChange("phone", e?.target?.value ?? "");
                }}
                placeholder={t("fields.phone")}
              />
            </Field>
          </HStack>
          <HStack>
            <Field
              label={t('wrappers.nonMandatoryLabel', { value: t('fields.division') })}
              errors={formFieldErrors.division}
            >
              <Select.Root
                collection={divisionsCollection}
                value={formData.division ? [formData.division] : []}
                onValueChange={(e) => {
                  const division = e.value?.[0] ?? "";
                  const clubBelongsToDivision = clubs.some((club) => (
                    club.label === formData.club &&
                    (club.labelDivision === division || club.division?.label === division)
                  ));

                  handleInputChanges({
                    division,
                    club: clubBelongsToDivision ? formData.club : "",
                  });
                }}
              >
                <Select.HiddenSelect />
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText
                      placeholder={t("sentences.selectEntity", {
                        entity: t("fields.division"),
                      })}
                    />
                  </Select.Trigger>
                  <Select.IndicatorGroup>
                    <Select.Indicator />
                  </Select.IndicatorGroup>
                </Select.Control>
                <Portal>
                  <Select.Positioner>
                    <Select.Content>
                      {divisionsCollection.items.map((division) => (
                        <Select.Item item={division} key={division.value}>
                          {division.label}
                          <Select.ItemIndicator />
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Portal>
              </Select.Root>
            </Field>
            <Field
              label={t('wrappers.nonMandatoryLabel', { value: t('fields.club') })}
              errors={formFieldErrors.club}
            >
              <Select.Root
                collection={clubsCollection}
                disabled={!formData.division}
                value={formData.club ? [formData.club] : []}
                onValueChange={(e) => {
                  handleInputChange("club", e.value?.[0] ?? "");
                }}
              >
                <Select.HiddenSelect />
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText
                      placeholder={t("sentences.selectEntity", {
                        entity: t("fields.club"),
                      })}
                    />
                  </Select.Trigger>
                  <Select.IndicatorGroup>
                    <Select.Indicator />
                  </Select.IndicatorGroup>
                </Select.Control>
                <Portal>
                  <Select.Positioner>
                    <Select.Content>
                      {clubsCollection.items.map((club) => (
                        <Select.Item item={club} key={club.value}>
                          {club.label}
                          <Select.ItemIndicator />
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Portal>
              </Select.Root>
            </Field>
            <Field
              label={t('wrappers.nonMandatoryLabel', { value: t('fields.salary') })}
              errors={formFieldErrors.salaryRange}
            >
              <Select.Root
                collection={salaryRangesCollection}
                value={
                  formData.salaryRange === null ? [] : [formData.salaryRange]
                }
                onValueChange={(e) => {
                  handleInputChange("salaryRange", e.value?.[0] ?? null);
                }}
              >
                <Select.HiddenSelect />
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText
                      placeholder={t("sentences.selectEntity", {
                        entity: t("fields.salary"),
                      })}
                    />
                  </Select.Trigger>
                  <Select.IndicatorGroup>
                    <Select.Indicator />
                  </Select.IndicatorGroup>
                </Select.Control>
                <Portal>
                  <Select.Positioner>
                    <Select.Content>
                      {salaryRangesCollection.items.map((salaryRange) => (
                        <Select.Item item={salaryRange} key={salaryRange.value}>
                          {salaryRange.label}
                          <Select.ItemIndicator />
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Portal>
              </Select.Root>
            </Field>
          </HStack>
          <Field
            label={t('wrappers.nonMandatoryLabel', { value: t('fields.role') })}
            errors={formFieldErrors.roleId}
          >
            <Select.Root
              collection={rolesCollection}
              value={formData.roleId === null ? [] : [formData.roleId]}
              onValueChange={(e) => {
                handleInputChange("roleId", e.value?.[0] ?? null);
              }}
            >
              <Select.HiddenSelect />
              <Select.Control>
                <Select.Trigger>
                  <Select.ValueText
                    placeholder={t("sentences.selectEntity", {
                      entity: t("fields.role"),
                    })}
                  />
                </Select.Trigger>
                <Select.IndicatorGroup>
                  <Select.Indicator />
                </Select.IndicatorGroup>
              </Select.Control>
              <Portal>
                <Select.Positioner>
                  <Select.Content>
                    {rolesCollection.items.map((role) => (
                      <Select.Item item={role} key={role.value}>
                        {role.label}
                        <Select.ItemIndicator />
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Portal>
            </Select.Root>
          </Field>
          <Field
            label={t('wrappers.nonMandatoryLabel', { value: t('fields.authorization') })}
            errors={formFieldErrors.authorization}
          >
            <Switch.Root
              size="lg"
              id="authorization"
              checked={formData.authorization}
              onCheckedChange={(e) =>
                handleInputChange("authorization", Boolean(e?.checked))
              }
            >
              <Switch.HiddenInput />
              <Switch.Control />
              <Switch.Label />
            </Switch.Root>
          </Field>
          <Field
            label={t('wrappers.mandatoryLabel', { value: t('fields.email') })}
            errors={formFieldErrors.email}
            display="flex"
            alignItems="center"
            mb="2vh"
          >
            <Input
              value={formData.email ?? ""}
              onChange={(e) => {
                handleInputChange("email", e?.target?.value ?? "");
              }}
              placeholder={t("users.userEmail")}
              type="email"
            />
          </Field>
          {
            user?.activated &&
              <CertificatesInput
                  userId={userId}
                  onRefresh={() => getUser(userId)}
                  certificates={user?.certificates ?? []}
                  language={t}
              />
          }
          <Field
            label={t('wrappers.nonMandatoryLabel', { value: t('fields.password') })}
            errors={formFieldErrors.password}
            display="flex"
            alignItems="center"
            mb="2vh"
          >
            <Input
              value={formData.password ?? ""}
              onChange={(e) => {
                handleInputChange("password", e?.target?.value ?? "");
              }}
              placeholder={t("users.userPassword")}
              type="password"
            />
          </Field>
          {userId && user?.activated === true && (
            <VStack justify="flex-start" align="left">
              <Text fontWeight="500">{t("users.partnerMail")}</Text>
              <HStack>
                <Text marginRight={10}>
                  {user.firstname
                    .trim()
                    .toLowerCase()
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, "")
                    .replace(/\s+/g, "")[0] +
                    "." +
                    user.lastname
                      .trim()
                      .toLowerCase()
                      .normalize("NFD")
                      .replace(/[\u0300-\u036f]/g, "")
                      .replace(/\s+/g, "") +
                    "@partners-basketballplayersunion.com"}
                </Text>
                <HStack justify="flex-start" align="left">
                  {checkPartners?.found &&
                  checkPartners?.redirection?.targets?.[0]?.state === "active" ? (
                    <Text fontWeight="500">Accepté</Text>
                  ) : (
                    <Text fontWeight="500">En attente d'acceptation</Text>
                  )}
                </HStack>
                {checkPartners?.found &&
                  checkPartners?.redirection?.targets?.[0]?.state !== "active" &&
                  (isResent ? (
                    <Text>Message envoyé</Text>
                  ) : (
                    <Button
                      variant="primary"
                      mt="auto"
                      height="auto"
                      borderRadius=".5rem"
                      style={{ padding: "1rem 1.5rem" }}
                      onClick={() => handleResend()}
                    >
                      Renvoyer
                    </Button>
                  ))}
              </HStack>
            </VStack>
          )}
        </VStack>
      ) : (
        <VStack flex={1} w="100%" align="center" justify="flex-start">
          <Spinner size="lg" />
        </VStack>
      )}
      <Button
        variant="primary"
        mt="auto"
        height="auto"
        borderRadius=".5rem"
        style={{ padding: "1rem 1.5rem" }}
        onClick={() =>
          handleFormSubmit(async (data) => {
            if (removeDeletedAt) {
              data.deletedAt = null;
            }
            if (userId) {
              return api.ApiService.update("user", userId, data);
            }
            return api.ApiService.create("user", data);
          }, props?.successCallback)
        }
      >
        {t("actions.validate")}
      </Button>
    </VStack>
  );
};

export default CreateOrEditUser;
